import getConfig from 'next/config';
import { useEffect, useMemo, useState } from 'react';

import cx from 'classnames';
import moment from 'moment';

import {
  BarsIcon,
  TimesIcon,
  EditIcon,
  SignOutAccountIcon,
  SignOutIcon,
  MessageIcon,
} from 'components/Icons';
import { Link } from 'components/Layout';
import { Drawer } from 'components/Layout/Drawer/Drawer';
import { useAuth } from 'contexts/AuthContext';

import PasswordExpiryDropdown from '../PasswordExpiryDropdown/PasswordExpiryDropdown';
import UserAvatar from '../UserAvatar/UserAvatar';
import UserDropdown from '../UserDropdown/UserDropdown';

import s from './Nav.module.scss';

const {
  publicRuntimeConfig: { appVersion, changePasswordUrl },
} = getConfig();

const Nav: React.FC = () => {
  const [menuIsActive, toggleMenuIsActive] = useState(false);
  const [userIsActive, toggleUserIsActive] = useState(false);
  const [showMessagesDrawer, setShowMessagesDrawer] = useState(false);

  const {
    state: { user },
    actions: { logout },
  } = useAuth();

  useEffect(() => {
    if (menuIsActive) {
      document.body.style.overflow = 'hidden';
      return;
    }
    document.body.style.overflow = 'unset';
  }, [menuIsActive]);

  const passwordIsExpired = useMemo(
    () => moment().isSameOrAfter(user?.passwordExpiryDate),
    [user],
  );

  const passwordWillExpire = useMemo(() => {
    const diff = moment(user?.passwordExpiryDate).diff(moment(), 'days');

    return diff <= 3 && diff >= 0 && !passwordIsExpired; // 3 days before expiry
  }, [user, passwordIsExpired]);

  return (
    <nav>
      <button
        className={s.toggleNav}
        onClick={() => {
          if (userIsActive) {
            toggleUserIsActive(!userIsActive);
          }
          toggleMenuIsActive(!menuIsActive);
        }}
        aria-expanded={menuIsActive}
        type="button"
      >
        {menuIsActive ? <TimesIcon /> : <BarsIcon />}
      </button>

      <div className="d-flex">
        <ul
          className={cx(s.navWrapper, {
            [s.active]: menuIsActive,
          })}
        >
          <li className="d-block d-md-none">
            <h4 className="mb-0 font-weight-bold">Main Menu</h4>
          </li>

          {user && (
            <li
              className="mr-md-3"
              role="presentation"
              onClick={() => toggleMenuIsActive(false)}
            >
              <Link href="/" passHref className={s.link}>
                Launchpad
              </Link>
            </li>
          )}

          <li
            className="mr-md-3"
            role="presentation"
            onClick={() => toggleMenuIsActive(false)}
          >
            <Link href="/" passHref className={s.link}>
              Home Page
            </Link>
          </li>

          <li
            className="mr-md-3"
            role="presentation"
            onClick={() => toggleMenuIsActive(false)}
          >
            <Link href="/contact-us" passHref className={s.link}>
              Contact us
            </Link>
          </li>

          <li
            className="mr-md-3"
            style={{ cursor: 'pointer' }}
            role="presentation"
            onClick={() => {
              setShowMessagesDrawer(true);
              toggleMenuIsActive(false);
            }}
          >
            <div
              style={{
                padding: '0.5rem 0.8rem',
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'center',
                borderRadius: '8px',
                gap: '5px',
              }}
            >
              <MessageIcon size={2} />
              <div
                style={{
                  width: '25px',
                  height: '25px',
                  color: 'white',
                  borderRadius: '9999px',
                  backgroundColor: '#e05252',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '13px',
                  fontWeight: 600,
                  lineHeight: 1,
                }}
              >
                3
              </div>
            </div>
          </li>

          {user && passwordIsExpired && (
            <li className="mr-md-3 d-none d-md-block">
              <PasswordExpiryDropdown>
                <p className="font-weight-bold">Your password is expired</p>
              </PasswordExpiryDropdown>
            </li>
          )}

          {user && passwordWillExpire && (
            <li className="mr-md-3 d-none d-md-block">
              <PasswordExpiryDropdown>
                <p className="font-weight-bold">
                  Your password is about to expire
                </p>

                <p>
                  Your password will expire in 24 hours. Once your password has
                  expired, you will not be able to log in until your password
                  has reset.
                </p>
              </PasswordExpiryDropdown>
            </li>
          )}
        </ul>

        {user && (
          <>
            <ul
              className={cx('d-block d-md-none', s.navWrapper, s.user, {
                [s.active]: userIsActive,
              })}
            >
              <li className="d-block d-md-none">
                <span className="d-flex align-items-center">
                  <UserAvatar user={user} />
                  <span className="ml-4 font-weight-bold">{user.name}</span>
                </span>
              </li>

              <li className="d-block d-md-none">
                <a href={changePasswordUrl} className="text-dark">
                  <span className="d-flex justify-content-between align-items-center">
                    <span>Change password</span> <EditIcon />
                  </span>
                </a>
              </li>

              <li className="d-block d-md-none">
                <span
                  className="d-flex justify-content-between align-items-center"
                  role="presentation"
                  onClick={() => logout()}
                >
                  <span>Logout Launchpad</span>
                  <SignOutIcon />
                </span>
              </li>

              <li className="d-block d-md-none">
                <span
                  className="d-flex justify-content-between align-items-center"
                  role="presentation"
                  onClick={() => logout(true)}
                >
                  <span>Logout Account</span>
                  <SignOutAccountIcon />
                </span>
              </li>

              <div
                className={cx('d-block d-md-none text-right p-2', s.version)}
              >
                {appVersion}
              </div>
            </ul>

            <div
              onClick={() => {
                if (menuIsActive) {
                  toggleMenuIsActive(!menuIsActive);
                }

                toggleUserIsActive(!userIsActive);
              }}
              role="presentation"
            >
              <span className={cx('d-block d-md-none', s.userAvatar)}>
                <UserAvatar user={user} isActive={userIsActive} />
              </span>
            </div>
          </>
        )}

        {/* TODO: remove userDropdown and re-use menu above */}
        {user && (
          <span className={cx('d-none d-md-block', s.userAvatar)}>
            <UserDropdown user={user} />
          </span>
        )}
      </div>

      <Drawer show={showMessagesDrawer}>
        <div onClick={() => setShowMessagesDrawer(false)} role="presentation">
          <div
            style={{
              display: 'flex',
              alignContent: 'center',
              gap: '1rem',
              padding: '1.5rem',
              borderBottom: '1px solid var(--grey)',
              alignItems: 'center',
            }}
          >
            <div style={{ cursor: 'pointer' }}>
              <TimesIcon />
            </div>

            <h5 style={{ fontWeight: 'bold', marginBottom: '0' }}>Messages</h5>
            <div
              style={{
                width: '25px',
                height: '25px',
                color: 'white',
                borderRadius: '9999px',
                backgroundColor: '#e05252',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: 600,
                lineHeight: 1,
              }}
            >
              3
            </div>
          </div>

          <div style={{ padding: '0 1rem' }}>
            {[
              { name: 'Jair Santo', time: '2 days ago' },
              { name: 'Paula Antonia', time: '1 week ago' },
              { name: 'Mauricio Adao', time: '1 week ago' },
            ].map(msg => (
              <div
                key={msg.name}
                style={{
                  display: 'flex',
                  gap: '0.75rem',
                  alignItems: 'flex-start',
                  borderBottom: '1px solid var(--light)',
                  padding: '1rem 0',
                }}
              >
                <div style={{ flexShrink: 0 }}>
                  <UserAvatar
                    user={{
                      name: msg.name,
                      user_email: '',
                      upn: '',
                      oid: '',
                      adapter: '',
                      groups: [''],
                      lastSignIn: '',
                      passwordExpiryDate: '',
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}
                  >
                    Claim 123456 for your attention.
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: '0.75rem',
                      alignItems: 'center',
                      marginBottom: '0.35rem',
                    }}
                  >
                    <span
                      style={{ color: 'var(--text-grey)', fontSize: '0.8rem' }}
                    >
                      {msg.time}
                    </span>
                    <a
                      className="text-primary"
                      style={{ cursor: 'pointer', fontSize: '0.8rem' }}
                    >
                      Mark as read
                    </a>
                  </div>
                  <a
                    className="text-primary"
                    style={{ cursor: 'pointer', fontSize: '0.8rem' }}
                  >
                    Click here to review.
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Drawer>
    </nav>
  );
};

export default Nav;
